<script>
import Layout from '@layouts/anonymous'
import { authMethods } from '@state/helpers'
import constant from '@constants/constant'
import { hmdaLevels } from '@/src/utils/accesslevels'
import AuthFormHeader from './auth-form-header'

export default {
  page: {
    meta: [{ name: 'description', content: `Log in to ${constant.title}` }],
  },
  components: { Layout, AuthFormHeader },
  data() {
    return {
      headerTitle: 'Sign in',
      usernameError: '',
      passwordError: '',
      username: '',
      password: '',
      passwordToggle: false,
      authError: null,
      errormessage: '',
      tryingToLogIn: false,
      selectedValue: '',
      accountLocked: false,
      showSpinner: true,
      showSessionAlert: false,
    }
  },

  computed: {
    placeholders() {
      return {
        usernameexample: 'Username',
        passwordexample: 'Password',
      }
    },
  },
  mounted() {
    this.logOut().finally(() => (this.showSpinner = false))
    this.$store.state.seletedCustomerInfo = []
    this.sessionTimedOut()
  },
  methods: {
    ...authMethods,
    encryptCredentials(value) {
      let encryptedBytes
      if(value) {
        encryptedBytes = this.$CryptoJS.AES.encrypt(value, this.$CryptoJS.enc.Utf8.parse(process.env.VUE_APP_CIPHER_KEY), {mode: this.$CryptoJS.mode.ECB, padding: this.$CryptoJS.pad.ZeroPadding})
      }
      return encryptedBytes.ciphertext.toString(this.$CryptoJS.enc.Base64)
    },
    tryToLogIn() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          // eslint-disable-next-line
          this.$emit('inputData', this.username)
          this.tryingToLogIn = true
        } else {
          this.tryingToLogIn = false
          return null
        }
        // Reset the authError if it existed.
        this.authError = null
        return this.logIn({
          // username: this.username,
          // password: this.password,
          username: this.encryptCredentials(this.username),
          password: this.encryptCredentials(this.password),
        })
          .then((response) => {
            this.tryingToLogIn = false
            if (response.statusCode === 200) {
              // Redirect to the originally requested page, or to the home page
              this.$router.push(
                this.$route.query.redirectFrom || {
                  name: hmdaLevels.isHmdaUser() ? 'hmda' : 'home',
                }
              )
              // tokenExpiryTrack(this.$store.state.auth.currentUser.expires_in)
            } else if (response.statusCode === 202) {
              let tokenId = response.value.externalId;
              this.$router.push({ name: 'createnewpassword', params:{tokenId: tokenId, showPasswordChangeAlert: true, username: this.username}});
            }
             else if (response.statusCode === 401) {
              this.authError = response.value.message
              this.errormessage = response.value.message
              if (
                response.value['ipAuthenticationViaOTP'] !== undefined &&
                response.value.ipAuthenticationViaOTP === true
              ) {
                this.$router.push({
                  name: 'ipwhitelistingotpoptions',
                  params: {
                    username: this.username,
                    emailid: response.value.email,
                    phone: response.value.phoneNumber,
                    countryid: response.value.countryId,
                    whitelistip: response.value.whitelistIPViaOTP,
                  },
                })
              }
              if (response.value.message === 'Your account is locked.') {
                this.accountLocked = true
              }
            }
          })
          .catch((error) => {
            this.authError = error.value.message
            this.errormessage = error.value.message
          })
      })
    },
    contactAccountAdministrator() {
      this.contactAdmin({
        username: this.username,
      })
        .then((response) => {
          this.errormessage = response.data.message
          this.accountLocked = false
        })
        .catch((error) => {
          this.tryingToLogIn = false
          this.authError = error.response.data.message
          this.errormessage = error.response.data.message
        })
    },
    sessionTimedOut() {
      if ('sessionTimedOut' in this.$route.query) {
        this.showSessionAlert = true
      }
    },
    reRoute() {
      this.$router.push({ name: 'login' })
    },
    handleForgotPassword() {
      this.$router.push({ name: 'forgotpassword', params: {userName: this.username}});
    }
  },
}
</script>

<template>
  <Layout>
    <BaseSpinner v-if="showSpinner" />
    <form :class="$style.form" @submit.prevent="tryToLogIn">
      <AuthFormHeader :title="headerTitle"/>
      <b-alert
        v-model="showSessionAlert"
        variant="info"
        dismissible
        @dismissed="reRoute"
        >You were logged out of Xactusphere because of inactivity.</b-alert
      >
      <BaseELabel size="md" label="Username"></BaseELabel>
      <BaseEInputText
        id="username"
        v-model="username"
        v-validate="'required'"
        field-id="username"
        name="username"
        :placeholder="placeholders.usernameexample"
        :class="{
          input: true,
          'is-danger': errors.has('username'),
        }"
      />
      <div
        v-show="errors.has('username')"
        class="error"
        style="margin: 5px auto"
        >Username is required</div
      >
      <BaseELabel size="md" label="Password"></BaseELabel>
      <b-input-group>
        <template v-slot:append>
          <b-input-group-text
            style=" cursor: pointer;background: transparent; border-left: 0"
          >
            <span type="password" @click="passwordToggle = !passwordToggle">
              <i
                :class="passwordToggle ? 'fas fa-eye-slash' : 'fas fa-eye'"
              ></i>
            </span>
          </b-input-group-text>
        </template>

        <b-form-input
          id="password"
          v-model="password"
          v-validate="'required'"
          style="border-right: 0"
          label-content="Password:"
          field-id="password"
          name="password"
          :type="passwordToggle ? 'text' : 'password'"
          :placeholder="placeholders.passwordexample"
          :class="{
            input: true,
            'is-danger': errors.has('password'),
          }"
        />
      </b-input-group>
      <div
        v-show="errors.has('password')"
        class="error"
        style="margin: 6px auto"
        >Password is required</div
      >
      <div
        :class="$style.link"
        class="col-12 text-right px-0 mb-0 py-3 border-bottom"
      >
        <b-link class="textUnderline" @click.prevent="handleForgotPassword">
          Forgot Password</b-link
        >
      </div>
      <b-row class='pt-3' align-h="end">
        <b-col class="col-auto">
          <BaseEButton :disabled="tryingToLogIn" type="submit"
            >Sign in</BaseEButton
          >
        </b-col>
      </b-row>
      <b-alert
        v-show="authError"
        show
        variant="danger"
        class="p-1"
        :class="$style.alert"
        >{{ errormessage }}</b-alert
      >
      <b-row v-if="accountLocked" class="mt-3">
        <b-col>
          <p class="text-center mt-3">
            <BaseEButton dense @click="contactAccountAdministrator">
              Click here
            </BaseEButton>
            to contact your Account Administrator to unlock.
          </p>
        </b-col>
      </b-row>
    </form>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.link {
  float: right;
  margin-top: $size-content-label-margin;
  margin-bottom: $size-content-label-margin;
  font-size: $size-content-font;
}

.alert {
  padding: 5px;
  margin-top: $size-content-label-margin;
}

.form {
  max-width: 480px;
  padding: 30px;
  margin: 0 auto;
  text-align: left;
  background: #fff;
  border-radius: 3px;
  -webkit-box-shadow: 0 0.2rem 1rem rgba(0, 0, 0, 0.05),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1);
  box-shadow: 0 0.2rem 1rem rgba(0, 0, 0, 0.05),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1);
}

.header {
  font-size: 2rem;
  font-weight: $label-font-weight;
  line-height: 1.2;
  color: rgb(33, 37, 41);
  text-align: left;
}

input:active,
input:focus {
  border-color: #dce0e4 !important;
  border-style: solid;
  outline: none !important;
  outline-width: 0 !important;
  -moz-box-shadow: none;
  -webkit-box-shadow: none;
  box-shadow: none !important;
}

.resendbtn {
  padding: 3px 8px;
  color: #fff !important;
  background-color: #4b868f;
  border: none;
  outline: none;
}

.resendbtn:hover {
  background-color: #53666d;
}

.resendbtn:active {
  background-color: #53666d !important;
}

.resendbtn:focus {
  background-color: #53666d !important;
}

input::-ms-clear,
input::-ms-reveal {
  display: none;
}
</style>
